import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Suspension from "@/pages/Suspension";
import MyConsent from "@/pages/MyConsent";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import ManagementOverview from "@/pages/ManagementOverview";
import EmployeeMaster from "@/pages/EmployeeMaster";
import TaskAllocation from "@/pages/TaskAllocation";
import Attendance from "@/pages/Attendance";
import QualityCheck from "@/pages/QualityCheck";
import Performance from "@/pages/Performance";
import Workspace from "@/pages/Workspace";
import Reports from "@/pages/Reports";
import Leaves from "@/pages/Leaves";
import IDCards from "@/pages/IDCards";
import Invoices from "@/pages/Invoices";
import Certificates from "@/pages/Certificates";
import Payslips from "@/pages/Payslips";
import SalaryPayouts from "@/pages/Payouts";
import SecurityActivity from "@/pages/SecurityActivity";
import Loans from "@/pages/Loans";
import AuditTrail from "@/pages/AuditTrail";
import EmployeeProfile from "@/pages/EmployeeProfile";
import ITSupport from "@/pages/ITSupport";
import TeamChat from "@/pages/TeamChat";
import { CallProvider } from "@/components/CallManager";
import { LOGO } from "@/lib/helpers";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
      <img src={LOGO} alt="VNS" className="w-20 h-20 object-contain mb-4 animate-pulse" />
      <p className="text-navy font-heading">Loading workspace…</p>
    </div>
  );
}

function Protected({ children, adminOnly, founderOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (founderOnly && user.role !== "founder") return <Navigate to="/dashboard" replace />;
  if (adminOnly && !["founder", "admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <Protected>
            <CallProvider>
              <Layout />
            </CallProvider>
          </Protected>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/dashboard" element={<ExecutiveDashboard />} />
        <Route path="/management" element={<Protected adminOnly><ManagementOverview /></Protected>} />
        <Route path="/employees" element={<Protected adminOnly><EmployeeMaster /></Protected>} />
        <Route path="/id-cards" element={<IDCards />} />
        <Route path="/certificates" element={<Certificates />} />
        <Route path="/tasks" element={<TaskAllocation />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/payslips" element={<Payslips />} />
        <Route path="/payouts" element={<SalaryPayouts />} />
        <Route path="/security" element={<SecurityActivity />} />
        <Route path="/profile" element={<EmployeeProfile />} />
        <Route path="/it-support" element={<ITSupport />} />
        <Route path="/chat" element={<TeamChat />} />
        <Route path="/audit" element={<Protected founderOnly><AuditTrail /></Protected>} />
        <Route path="/loans" element={<Loans />} />
        <Route path="/suspension" element={<Protected founderOnly><Suspension /></Protected>} />
        <Route path="/consent" element={<MyConsent />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/leaves" element={<Leaves />} />
        <Route path="/quality" element={<QualityCheck />} />
        <Route path="/performance" element={<Performance />} />
        <Route path="/workspace" element={<Workspace />} />
        <Route path="/reports" element={<Reports />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
